import { useContext } from 'react'
import { t, plural } from '@lingui/core/macro'
import DeleteIcon from '@mui/icons-material/Delete'
import { CircularProgress, darken } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { MotionFab } from '@components'
import { modalContext } from '@contexts'
import { colors } from '@global/constants'

interface Props {
  numSelectedRows: number
  removingRows: boolean
  removeRows: () => Promise<void>
}

const useStyles = makeStyles(() => ({
  custom: {
    '&&': {
      backgroundColor: colors.red.main,
      '&:hover': {
        backgroundColor: darken(colors.red.main, 0.3),
      },
    },
  },
}))

/**
 * The remove rows button in the read grid header
 *
 * @param props React props
 * @param {number} props.numSelectedRows The number of rows selected for removal
 * @param {boolean} props.removingRows True if the user has sent a request to remove rows
 * @param {func} props.removeRows Function to remove the rows from the dataset
 * @returns
 */
export const RemoveRows = ({ numSelectedRows, removingRows, removeRows }: Props) => {
  const classes = useStyles()
  const { displayModal } = useContext(modalContext)
  const disabled = removingRows || numSelectedRows === 0
  const removeRowsText = plural(numSelectedRows, {
    one: `Remove ${numSelectedRows} row`,
    other: `Remove ${numSelectedRows} rows`,
  })
  const title = removingRows ? t`Sending row removal request` : removeRowsText
  const icon = removingRows ? <CircularProgress size={15} /> : <DeleteIcon />

  return (
    <MotionFab
      tooltipTitle={title}
      size="medium"
      disabled={disabled}
      className={classes.custom}
      onClick={handleClick}
      data-testid="remove-rows"
    >
      {icon}
    </MotionFab>
  )

  function handleClick(): void {
    displayModal({
      title: t`Are you sure?`,
      description: t`These rows will be permanently removed from the dataset`,
      confirm: () => () => removeRows(),
    })
  }
}
